import React, { useState, useEffect } from 'react';
import { apiDelete, apiGet, apiPost, apiPut } from '../api';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  User, Upload, Star, Trash2, Loader2, Mail, Phone, MapPin, Save
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { formatApiError } from '../lib/apiError';
import { compressImage } from '../lib/imageCompress';
import { MFASettingsCard } from '../components/MFASettingsCard';

// Irish counties
const IRISH_COUNTIES = [
  'Antrim', 'Armagh', 'Carlow', 'Cavan', 'Clare', 'Cork', 'Derry', 'Donegal',
  'Down', 'Dublin', 'Fermanagh', 'Galway', 'Kerry', 'Kildare', 'Kilkenny',
  'Laois', 'Leitrim', 'Limerick', 'Longford', 'Louth', 'Mayo', 'Meath',
  'Monaghan', 'Offaly', 'Roscommon', 'Sligo', 'Tipperary', 'Tyrone',
  'Waterford', 'Westmeath', 'Wexford', 'Wicklow'
];

// Eircode validation (Irish postal code format: A65 F4E2 or A65F4E2)
const validateEircode = (eircode) => {
  if (!eircode) return true; // Optional field
  const pattern = /^[A-Za-z]\d{2}\s?[A-Za-z0-9]{4}$/;
  return pattern.test(eircode.trim());
};

// Mobile number validation (+353 8X XXXXXXX)
const validateMobileNumber = (mobile) => {
  if (!mobile) return true; // Optional field
  const pattern = /^\+353\s?8[0-9]\s?[0-9]{3}\s?[0-9]{4}$/;
  return pattern.test(mobile.replace(/\s+/g, ' ').trim());
};

// Format mobile number as user types
const formatMobileNumber = (value) => {
  // Remove all non-digits except +
  let digits = value.replace(/[^\d+]/g, '');
  
  // If doesn't start with +353, add it
  if (!digits.startsWith('+353') && !digits.startsWith('+')) {
    if (digits.startsWith('353')) {
      digits = '+' + digits;
    } else if (digits.startsWith('08')) {
      digits = '+353' + digits.substring(1);
    } else if (digits.startsWith('8')) {
      digits = '+353' + digits;
    }
  }
  
  // Format as +353 8X XXX XXXX
  if (digits.startsWith('+353')) {
    const rest = digits.substring(4);
    if (rest.length <= 2) {
      return '+353 ' + rest;
    } else if (rest.length <= 5) {
      return '+353 ' + rest.substring(0, 2) + ' ' + rest.substring(2);
    } else {
      return '+353 ' + rest.substring(0, 2) + ' ' + rest.substring(2, 5) + ' ' + rest.substring(5, 9);
    }
  }
  
  return value;
};

export const ProfilePage = () => {
  const { api, user, refreshUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [favourites, setFavourites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    mobile: '',
    address: '',
    city: '',
    county: '',
    eircode: '',
    marketing_opt_in: false
  });
  
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const [profileRes, favouritesRes] = await Promise.all([
        apiGet('/api/profile'),
        apiGet('/api/favourites')
      ]);
      setProfile(profileRes);
      setFavourites(favouritesRes);
      setFormData({
        first_name: profileRes.first_name || '',
        last_name: profileRes.last_name || '',
        mobile: profileRes.phone || profileRes.mobile || '',
        address: profileRes.address || '',
        city: profileRes.city || '',
        county: profileRes.county || '',
        eircode: profileRes.postal_code || profileRes.eircode || '',
        marketing_opt_in: profileRes.marketing_opt_in || false
      });
    } catch (e) {
      console.error('Failed to fetch profile:', e);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    
    // Validate fields
    const newErrors = {};
    if (formData.mobile && !validateMobileNumber(formData.mobile)) {
      newErrors.mobile = 'Please enter a valid Irish mobile number (+353 8X XXX XXXX)';
    }
    if (formData.eircode && !validateEircode(formData.eircode)) {
      newErrors.eircode = 'Please enter a valid Eircode (e.g., A65 F4E2)';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error('Please fix the validation errors');
      return;
    }
    
    setErrors({});
    setSaving(true);
    try {
      // Map to backend field names
      await apiPut('/api/profile', { body: {
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.mobile, // Backend still uses 'phone'
        address: formData.address,
        city: formData.city,
        county: formData.county,
        postal_code: formData.eircode, // Backend still uses 'postal_code'
        marketing_opt_in: formData.marketing_opt_in
      } });
      toast.success('Profile updated successfully');
      if (refreshUser) refreshUser();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to update profile'));
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      // Iter-70 (P3): client-side compression to keep avatars under the
      // 1MB nginx-ingress ceiling. HEIC/HEIF phone photos get transcoded to
      // JPEG. Falls back to the raw file if compression throws.
      let uploadFile = file;
      try { uploadFile = await compressImage(file); } catch (_) { /* fall through */ }

      const formDataUpload = new FormData();
      formDataUpload.append('file', uploadFile);
      
      const data = await apiPost('/api/profile/photo', { body: formDataUpload });

      setProfile({ ...profile, photo_url: data.url });
      toast.success('Photo uploaded successfully');
      if (refreshUser) refreshUser();
    } catch (e) {
      const status = e?.status || e?.response?.status;
      const msg = status === 413
        ? 'That image is too large. Please try a smaller photo (or take a new one and try again).'
        : formatApiError(e, 'Failed to upload photo');
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveFavourite = async (studioId) => {
    try {
      await apiDelete('/api/favourites/{studio_id}', { params: { studio_id: studioId } });
      setFavourites(favourites.filter(f => f.id !== studioId));
      toast.success('Removed from favourites');
    } catch (e) {
      toast.error('Failed to remove favourite');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="profile-page">
        <div>
          <h1 className="text-3xl font-bold mb-2">My Profile</h1>
          <p className="text-muted-foreground">Manage your account settings and preferences</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Profile Photo */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-base">Profile Photo</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col items-center">
              {profile?.photo_url ? (
                <img 
                  src={profile.photo_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${profile.photo_url}` : profile.photo_url}
                  alt="Profile"
                  className="w-32 h-32 rounded-full object-cover mb-4"
                  data-testid="profile-photo"
                />
              ) : (
                <div className="w-32 h-32 rounded-full bg-muted flex items-center justify-center mb-4">
                  <User className="w-16 h-16 text-muted-foreground/50" />
                </div>
              )}
              <div className="relative">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handlePhotoUpload}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  disabled={uploading}
                  data-testid="photo-upload-input"
                />
                <Button variant="outline" disabled={uploading}>
                  {uploading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  Upload Photo
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">JPEG, PNG or WebP, max 5MB</p>
            </CardContent>
          </Card>

          {/* Profile Form */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Personal Information</CardTitle>
              <CardDescription>Update your account details</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSaveProfile} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name">First Name</Label>
                    <Input
                      id="first_name"
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      data-testid="first-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name">Last Name</Label>
                    <Input
                      id="last_name"
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      data-testid="last-name-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    value={profile?.email || ''}
                    disabled
                    className="bg-muted"
                  />
                  <p className="text-xs text-muted-foreground">Email cannot be changed</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="mobile">
                    <Phone className="w-4 h-4 inline mr-1" />
                    Mobile Number
                  </Label>
                  <Input
                    id="mobile"
                    type="tel"
                    value={formData.mobile}
                    onChange={(e) => {
                      const formatted = formatMobileNumber(e.target.value);
                      setFormData({ ...formData, mobile: formatted });
                      if (errors.mobile) setErrors({ ...errors, mobile: null });
                    }}
                    placeholder="+353 8X XXX XXXX"
                    className={errors.mobile ? 'border-destructive' : ''}
                    data-testid="mobile-input"
                  />
                  {errors.mobile && (
                    <p className="text-xs text-destructive">{errors.mobile}</p>
                  )}
                  <p className="text-xs text-muted-foreground">Format: +353 8X XXX XXXX</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address">
                    <MapPin className="w-4 h-4 inline mr-1" />
                    Address
                  </Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="123 Main Street"
                    data-testid="address-input"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city">City/Town</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      data-testid="city-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="county">County</Label>
                    <Select 
                      value={formData.county} 
                      onValueChange={(v) => setFormData({ ...formData, county: v })}
                    >
                      <SelectTrigger data-testid="county-select">
                        <SelectValue placeholder="Select county" />
                      </SelectTrigger>
                      <SelectContent>
                        {IRISH_COUNTIES.map((county) => (
                          <SelectItem key={county} value={county}>{county}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="eircode">Eircode</Label>
                  <Input
                    id="eircode"
                    value={formData.eircode}
                    onChange={(e) => {
                      const value = e.target.value.toUpperCase();
                      setFormData({ ...formData, eircode: value });
                      if (errors.eircode) setErrors({ ...errors, eircode: null });
                    }}
                    placeholder="A65 F4E2"
                    className={errors.eircode ? 'border-destructive' : ''}
                    maxLength={8}
                    data-testid="eircode-input"
                  />
                  {errors.eircode && (
                    <p className="text-xs text-destructive">{errors.eircode}</p>
                  )}
                  <p className="text-xs text-muted-foreground">Irish postal code format</p>
                </div>

                <div className="flex items-center justify-between p-4 rounded-lg border">
                  <div className="space-y-0.5">
                    <Label htmlFor="marketing">
                      <Mail className="w-4 h-4 inline mr-1" />
                      Marketing Communications
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Receive promotional emails from your favourite studios
                    </p>
                  </div>
                  <Switch
                    id="marketing"
                    checked={formData.marketing_opt_in}
                    onCheckedChange={(checked) => setFormData({ ...formData, marketing_opt_in: checked })}
                    data-testid="marketing-toggle"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Note: This does not affect class reminders, booking confirmations, or waitlist notifications
                </p>

                <Button type="submit" disabled={saving} data-testid="save-profile-btn">
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save Changes
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Iter-46ah: Two-Factor Authentication */}
        <MFASettingsCard />

        {/* Favourite Studios */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-500" />
              Favourite Studios ({favourites.length})
            </CardTitle>
            <CardDescription>Manage your favourite studios for quick access</CardDescription>
          </CardHeader>
          <CardContent>
            {favourites.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {favourites.map((studio) => (
                  <div 
                    key={studio.id}
                    className="flex items-center justify-between p-4 rounded-lg border"
                    data-testid={`favourite-studio-${studio.id}`}
                  >
                    <div className="flex items-center gap-3">
                      {studio.logo_url ? (
                        <img 
                          src={studio.logo_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${studio.logo_url}` : studio.logo_url}
                          alt={studio.name}
                          className="w-10 h-10 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <span className="text-primary font-bold">{studio.name[0]}</span>
                        </div>
                      )}
                      <div>
                        <Link 
                          to={`/studio/${studio.slug || studio.id}`}
                          className="font-medium hover:text-primary transition-colors"
                        >
                          {studio.name}
                        </Link>
                        {studio.rating && (
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                            {studio.rating}
                          </div>
                        )}
                      </div>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => handleRemoveFavourite(studio.id)}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Star className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
                <p className="text-muted-foreground mb-4">No favourite studios yet</p>
                <Link to="/studios">
                  <Button variant="outline">Browse Studios</Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};
